import classNames from 'classnames';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {VM} from '@hetao/scratch-core';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import Controls from '../../containers/controls.jsx';
import {getStageDimensions} from '../../lib/screen-utils';
import {STAGE_SIZE_MODES} from '../../lib/layout-constants';

import unFullScreenIcon from './icon--unfullscreen.svg';
import unFullScreenHoverIcon from './icon--unfullscreen--hover.svg';

import iconFont from '../../css/iconfont.css';
import styles from './stage-header.css';

const messages = defineMessages({
    largeStageSizeMessage: {
        defaultMessage: 'Switch to large stage',
        description: 'Button to change stage size to large',
        id: 'gui.stageHeader.stageSizeLarge'
    },
    smallStageSizeMessage: {
        defaultMessage: 'Switch to small stage',
        description: 'Button to change stage size to small',
        id: 'gui.stageHeader.stageSizeSmall'
    },
    fullStageSizeMessage: {
        defaultMessage: 'Enter full screen mode',
        description: 'Button to change stage size to full screen',
        id: 'gui.stageHeader.stageSizeFull'
    },
    unFullStageSizeMessage: {
        defaultMessage: 'Exit full screen mode',
        description: 'Button to get out of full screen mode',
        id: 'gui.stageHeader.stageSizeUnFull'
    },
    fullscreenControl: {
        defaultMessage: 'Full Screen Control',
        description: 'Button to enter/exit full screen mode',
        id: 'gui.stageHeader.fullscreenControl'
    }
});

const StageHeaderComponent = function (props) {
    const {
        isFullScreen,
        isPlayerOnly,
        mousePosition,
        onKeyPress,
        onSetStageLarge,
        onSetStageSmall,
        onSetStageFull,
        onSetStageUnFull,
        stageSizeMode,
        vm
    } = props;

    let header = null;

    if (isFullScreen) {
        const stageDimensions = getStageDimensions(null, true);
        header = (
            <Box className={styles.stageHeaderWrapperOverlay}>
                <Box
                    className={styles.stageMenuWrapper}
                    style={{width: stageDimensions.width}}
                >
                    <div className={styles.fullscreenLeftBtnGroup}>
                        <Button
                            className={styles.stageButton}
                            onClick={onSetStageUnFull}
                            onKeyPress={onKeyPress}
                        >
                            <img
                                alt={props.intl.formatMessage(messages.unFullStageSizeMessage)}
                                className={classNames(styles.stageButtonIcon, styles.stageButtonIconHover)}
                                draggable={false}
                                src={unFullScreenHoverIcon}
                                title={props.intl.formatMessage(messages.fullscreenControl)}
                            />
                            <img
                                alt={props.intl.formatMessage(messages.unFullStageSizeMessage)}
                                className={classNames(styles.stageButtonIcon, styles.stageButtonIconRegular)}
                                draggable={false}
                                src={unFullScreenIcon}
                                title={props.intl.formatMessage(messages.fullscreenControl)}
                            />
                        </Button>
                    </div>
                    <Controls vm={vm} />
                </Box>
            </Box>
        );
    } else {
        const stageControls =
            isPlayerOnly ? (
                []
            ) : (
                <div className={styles.stageSizeToggleGroup}>
                    <div>
                        <Button
                            className={classNames(
                                styles.stageButton,
                                styles.stageButtonLeft,
                                (stageSizeMode === STAGE_SIZE_MODES.small) ? null : styles.stageButtonToggledOff
                            )}
                            onClick={onSetStageSmall}
                        >
                            <i className={classNames(iconFont.iconfont, iconFont.iconXiaoping)} />
                        </Button>
                    </div>
                    <div>
                        <Button
                            className={classNames(
                                styles.stageButton,
                                styles.stageButtonRight,
                                (stageSizeMode === STAGE_SIZE_MODES.large) ? null : styles.stageButtonToggledOff
                            )}
                            onClick={onSetStageLarge}
                        >
                            <i className={classNames(iconFont.iconfont, iconFont.iconDaping)} />
                        </Button>
                    </div>
                </div>
            );
        header = (
            <Box className={styles.stageHeaderWrapper}>
                <Box className={styles.stageMenuWrapper}>
                    <div className={styles.stageSizeRow}>
                        <div>
                            <Button
                                className={classNames(styles.stageButton, styles.stageButtonFull)}
                                onClick={onSetStageFull}
                            >
                                <i className={classNames(iconFont.iconfont, iconFont.iconQuanping)} />
                            </Button>
                        </div>
                        {stageControls}
                        <div className={styles.stageSize}>
                            <span><i>{'x:'}</i>{`${mousePosition.x}`}</span>
                            <span><i>{'y:'}</i>{`${mousePosition.y}`}</span>
                        </div>
                    </div>
                    <Controls vm={vm} />
                </Box>
            </Box>
        );
    }

    return header;
};

const mapStateToProps = state => ({
    // This is the button's mode, as opposed to the actual current state
    stageSizeMode: state.scratchGui.stageSize.stageSize
});

StageHeaderComponent.propTypes = {
    intl: intlShape,
    isFullScreen: PropTypes.bool.isRequired,
    isPlayerOnly: PropTypes.bool.isRequired,
    mousePosition: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired
    }).isRequired,
    onKeyPress: PropTypes.func.isRequired,
    onSetStageFull: PropTypes.func.isRequired,
    onSetStageLarge: PropTypes.func.isRequired,
    onSetStageSmall: PropTypes.func.isRequired,
    onSetStageUnFull: PropTypes.func.isRequired,
    stageSizeMode: PropTypes.oneOf(Object.keys(STAGE_SIZE_MODES)),
    vm: PropTypes.instanceOf(VM).isRequired
};

StageHeaderComponent.defaultProps = {
    stageSizeMode: STAGE_SIZE_MODES.large,
    mousePosition: {
        x: 0,
        y: 0
    }
};

export default injectIntl(connect(
    mapStateToProps
)(StageHeaderComponent));
